<template>
  <!--
    FedEx EULA acceptance (Additional Guidance, section 2): the entire agreement is displayed in the app,
    the user must scroll through it, tick that they have read it and click the accept button before any
    FedEx credentials are requested on their behalf.
  -->
  <v-dialog :value="value" @input="$emit('input', $event)" max-width="880" scrollable persistent>
    <v-card rounded="lg">
      <v-card-title class="white">
        <FedExLogo :height="26" class="mr-3" />
        <div>
          <div>{{ title }}</div>
          <div class="text-caption grey--text text--darken-1">{{ form }} · Version {{ version }}</div>
        </div>
        <v-spacer />
        <v-btn icon @click="decline"><v-icon>mdi-close</v-icon></v-btn>
      </v-card-title>
      <v-card-text class="pa-0">
        <div ref="scroller" class="eula-scroll pa-6" @scroll="on_scroll">
          <!-- Revised date and version stay visible on every page of the agreement, as FedEx requires -->
          <div class="text-caption grey--text text--darken-1 mb-3">{{ form }} · Version {{ version }} · Revised June 2024</div>
          <pre class="eula-text">{{ text }}</pre>
        </div>
      </v-card-text>
      <v-divider />
      <v-card-actions class="flex-column align-stretch pa-4">
        <div v-if="!scrolled_to_end" class="text-caption grey--text text--darken-1 mb-2">
          <v-icon x-small left>mdi-arrow-down</v-icon>Scroll through the whole agreement to continue.
        </div>
        <v-checkbox
          v-model="acknowledged"
          :disabled="!scrolled_to_end"
          label="I have read and understood the FedEx End User License Agreement"
          hide-details
          class="mt-0 mb-3"
        />
        <div class="d-flex flex-wrap align-center">
          <v-btn text rounded small @click="download"><v-icon left small>mdi-download</v-icon>Download a copy</v-btn>
          <v-spacer />
          <v-btn text rounded class="mr-2" @click="decline">I decline</v-btn>
          <v-btn color="primary" rounded :disabled="!acknowledged" @click="accept">
            I accept the terms of FedEx EULA to start shipping
          </v-btn>
        </div>
        <FedExDisclaimer class="mt-3" />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { FEDEX_EULA_TEXT, FEDEX_EULA_VERSION, FEDEX_EULA_TITLE, FEDEX_EULA_FORM } from '@/assets/fedex_eula'
import FedExDisclaimer from '@/components/fedex/FedExDisclaimer'
import FedExLogo from '@/components/fedex/FedExLogo'

export default {
  name: 'FedExEula',
  components: { FedExDisclaimer, FedExLogo },
  props: {
    value: { type: Boolean, default: false },
  },
  data(){
    return {
      text: FEDEX_EULA_TEXT,
      title: FEDEX_EULA_TITLE,
      form: FEDEX_EULA_FORM,
      version: FEDEX_EULA_VERSION,
      scrolled_to_end: false,
      acknowledged: false,
    }
  },
  watch: {
    value(open){
      if(!open) return
      this.scrolled_to_end = false
      this.acknowledged = false
      this.$nextTick(() => {
        const el = this.$refs.scroller
        if(el) el.scrollTop = 0
        this.on_scroll()
      })
    }
  },
  methods: {
    on_scroll(){
      const el = this.$refs.scroller
      if(!el) return
      if(el.scrollTop + el.clientHeight >= el.scrollHeight - 8) this.scrolled_to_end = true
    },
    accept(){
      if(!this.acknowledged) return
      this.$emit('accepted', { accepted_at: new Date().toISOString(), version: `${this.form} ${this.version}` })
      this.$emit('input', false)
    },
    decline(){
      this.$emit('declined')
      this.$emit('input', false)
    },
    download(){
      const blob = new Blob([`${this.title}\n${this.form} · Version ${this.version}\n\n${this.text}`], { type: 'text/plain' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'FedEx End User License Agreement.txt'
      a.click()
      setTimeout(() => URL.revokeObjectURL(url), 1000)
    },
  },
}
</script>

<style scoped>
.eula-scroll {
  max-height: 55vh;
  overflow-y: auto;
}
.eula-text {
  white-space: pre-wrap;
  font-family: inherit;
  font-size: 13px;
  line-height: 1.5;
}
</style>
